/**
 * The template endpoints — slice 4's whole client surface.
 *
 *   listTemplates       GET    /templates                     the list, paginated
 *   fetchTemplate       GET    /templates/:id                 the document and its compiled HTML
 *   createTemplate      POST   /templates                     seeds a document that already has the footer
 *   updateTemplate      PUT    /templates/:id                 replaces the document; the API recompiles
 *   previewTemplate     POST   /templates/:id/preview         compiles a document — saved or unsaved
 *   setTemplateStatus   PATCH  /templates/:id/status          draft ↔ approved
 *   deleteTemplate      DELETE /templates/:id
 *   uploadTemplateAsset POST   /templates/:id/assets          an inline image
 *   deleteTemplateAsset DELETE /templates/:id/assets/:assetId any kind, including WhatsApp header media
 *   templateAssetPath          /templates/:id/assets/:assetId where its bytes are
 *
 * **Nothing here compiles anything.** There is no MJML in this app and there
 * must never be: the preview is a request, and the answer comes from the same
 * function that produced the HTML a campaign will send. A "quick client-side
 * preview" would be a second renderer, and a second renderer is a preview that
 * eventually disagrees with the email — at the moment the campaign has already
 * gone out. `previewTemplate` is therefore the only way this app turns blocks
 * into HTML, and it costs a round trip on purpose.
 *
 * **Both ends of every call are checked against the same contract.** Request
 * bodies are re-validated with the shared write schemas before they leave the
 * browser, so a document the API could only refuse never becomes a request; and
 * every response is parsed with the shared read shape, which is `.strict()` —
 * so an API that started returning `mjmlSource`, or a template without its
 * footer, would fail parsing here as well as there.
 *
 * **`PUT` for the document, `PATCH` only for the status.** The editor holds the
 * whole document and sends the whole document; that is what makes reorder,
 * delete and undo one code path instead of a diff protocol. The status is
 * genuinely one field, so it is the one thing patched.
 */
import {
  createTemplateRequestSchema,
  okResponseSchema,
  setTemplateStatusRequestSchema,
  templateListResponseSchema,
  templatePreviewRequestSchema,
  templatePreviewResponseSchema,
  templateResponseSchema,
  updateTemplateRequestSchema,
  uploadTemplateAssetResponseSchema,
  type CreateTemplateRequest,
  type OkResponse,
  type TemplateAssetKind,
  type SetTemplateStatusRequest,
  type Template,
  type TemplateAsset,
  type TemplateListQuery,
  type TemplateListResponse,
  type TemplatePreviewRequest,
  type TemplatePreviewResponse,
  type UpdateTemplateRequest,
} from '@wend/shared';

import { apiRequest } from './client';

/** Ids come from the API, but a path segment is never interpolated raw. */
function templatePath(templateId: string, suffix = ''): string {
  return `/templates/${encodeURIComponent(templateId)}${suffix}`;
}

/**
 * Where one inline image's bytes are, as a path this client can request.
 *
 * `templateAsset.url` from the API says the same thing with the `/api` prefix
 * already on it; this form is the one `apiRequest`/`apiBlob` take, because
 * `API_BASE_URL` supplies that prefix. Both are the same route — the asset is
 * served by us, under the tenant guard, which is the whole reason an image
 * block references an asset id and never a URL.
 */
export function templateAssetPath(templateId: string, assetId: string): string {
  return templatePath(templateId, `/assets/${encodeURIComponent(assetId)}`);
}

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

export async function listTemplates(
  query: TemplateListQuery,
  signal?: AbortSignal,
): Promise<TemplateListResponse> {
  return apiRequest({
    path: '/templates',
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      sort: query.sort,
      direction: query.direction,
      // An empty search is not a filter for "nothing"; it is no filter at all.
      ...(query.search === undefined || query.search === '' ? {} : { search: query.search }),
      ...(query.type === undefined ? {} : { type: query.type }),
      ...(query.status === undefined ? {} : { status: query.status }),
    },
    schema: templateListResponseSchema,
    signal,
  });
}

/** One template: the document, the compiled HTML, and its inline images. */
export async function fetchTemplate(templateId: string, signal?: AbortSignal): Promise<Template> {
  const response = await apiRequest({
    path: templatePath(templateId),
    schema: templateResponseSchema,
    signal,
  });
  return response.template;
}

/* -------------------------------------------------------------------------- */
/* Write                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Creates a template.
 *
 * `blocks` is deliberately not sent by the screen that calls this: omitted, the
 * API seeds `createDefaultDocument` — a heading, a paragraph and the unsubscribe
 * footer. That is how "every new template has the footer" stays true of a
 * caller which has never heard of a footer, and it is one less thing this app
 * can get wrong.
 */
export async function createTemplate(request: CreateTemplateRequest): Promise<Template> {
  const response = await apiRequest({
    path: '/templates',
    method: 'POST',
    body: createTemplateRequestSchema.parse(request),
    schema: templateResponseSchema,
  });
  return response.template;
}

/**
 * Replaces the document, the name, the subject, the preheader or the reply-to —
 * any subset of the five.
 *
 * `blocks` is a replacement rather than a patch, so a document that has lost its
 * footer is refused here (by the contract, before the request) and again there
 * (by the request schema, and again by the write path). The answer is the
 * template as it now stands, recompiled, so a caller never re-reads to find out
 * what its own save did.
 */
export async function updateTemplate(
  templateId: string,
  request: UpdateTemplateRequest,
): Promise<Template> {
  const response = await apiRequest({
    path: templatePath(templateId),
    method: 'PUT',
    body: updateTemplateRequestSchema.parse(request),
    schema: templateResponseSchema,
  });
  return response.template;
}

/**
 * Approves a draft, or sends an approved template back to draft.
 *
 * The API refuses an approval while anything is outstanding — no subject, no
 * postal address in the footer, an empty body, a variable a contact cannot
 * supply — and names each one in `fieldErrors` keyed by its blocker. The editor
 * shows those blockers before the button is pressed, but this is the check that
 * counts: the campaign wizard in slice 5 lists approved templates, so the gate
 * has to hold against anything that can call the API, not just against this
 * screen.
 */
export async function setTemplateStatus(
  templateId: string,
  request: SetTemplateStatusRequest,
): Promise<Template> {
  const response = await apiRequest({
    path: templatePath(templateId, '/status'),
    method: 'PATCH',
    body: setTemplateStatusRequestSchema.parse(request),
    schema: templateResponseSchema,
  });
  return response.template;
}

/** Deletes the template, its inline images and their stored bytes. */
export async function deleteTemplate(templateId: string): Promise<OkResponse> {
  return apiRequest({
    path: templatePath(templateId),
    method: 'DELETE',
    schema: okResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Preview                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Compiles a document — the stored one, or the one being edited — and answers
 * with the HTML, the substituted subject and preheader, the sample values it
 * used, and what still stops the template being approved.
 *
 * Two things about the request are worth knowing at the call site.
 *
 * **Omitting `blocks` previews the *stored* document.** With
 * `substituteSampleValues: false` as well, the answer is byte-identical to the
 * template's own `htmlCompiled` — the same function over the same inputs — which
 * is why the editor can *compare the two strings* and say so rather than
 * inviting someone to eyeball both. Sending `blocks` previews what is on screen,
 * which is what a person editing wants and which is no longer comparable with
 * anything stored.
 *
 * **A document with an unresolvable variable is a 400, not a preview.** The
 * request schema runs the same save-time refusal, so `{{ compnay }}` is refused
 * here — naming the token and the block it is in — before it can be saved. The
 * editor renders that failure against the block that caused it.
 */
export async function previewTemplate(
  templateId: string,
  request: TemplatePreviewRequest,
  signal?: AbortSignal,
): Promise<TemplatePreviewResponse> {
  return apiRequest({
    path: templatePath(templateId, '/preview'),
    method: 'POST',
    body: templatePreviewRequestSchema.parse(request),
    schema: templatePreviewResponseSchema,
    signal,
  });
}

/* -------------------------------------------------------------------------- */
/* Inline images                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Uploads an inline image against a template.
 *
 * Multipart, under the field name `file` the API's `FileInterceptor` expects.
 * `apiRequest` sends a `FormData` verbatim and leaves the `Content-Type` to the
 * browser, so the multipart boundary matches the body — the same path the
 * dataset upload takes.
 *
 * Nothing about the file is asserted here beyond what the input already
 * restricts. The type is decided by **sniffing the bytes** on the server, not by
 * the `Content-Type` the browser guessed from a file extension, so a `.png` that
 * is really something else is refused there and this function would only have
 * been guessing.
 */
export async function uploadTemplateAsset(
  templateId: string,
  file: File,
  kind: TemplateAssetKind = 'inline_image',
): Promise<TemplateAsset> {
  const body = new FormData();
  body.append('file', file);

  const response = await apiRequest({
    // A query parameter rather than a form field, because the server has to know
    // which byte ceiling to buffer under before it parses the body.
    path: `${templatePath(templateId, '/assets')}?kind=${encodeURIComponent(kind)}`,
    method: 'POST',
    body,
    schema: uploadTemplateAssetResponseSchema,
  });
  return response.asset;
}

/**
 * Removes one file from a template — of **any** kind, which is the point.
 *
 * The only way an attachment comes off: nothing references one, so nothing can
 * stop referencing it and the save-time sweep will never reach it. Slice 9 gave
 * a second kind that exact property — a WhatsApp header file is referenced by no
 * block either — and this route needed no change to serve it, because it deletes
 * by asset id and never by kind. `WaHeaderMediaPanel` calls it for the same
 * reason `AttachmentsPanel` does, and a WhatsApp-specific removal endpoint was
 * rejected: two routes deleting a row and its stored object are two chances for
 * the row and the object to fall out of step.
 */
export async function deleteTemplateAsset(
  templateId: string,
  assetId: string,
): Promise<OkResponse> {
  return apiRequest({
    path: `${templatePath(templateId, '/assets')}/${assetId}`,
    method: 'DELETE',
    schema: okResponseSchema,
  });
}
